import { useContext, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Table, Button, Modal, Form, Input, Select, Switch, Space, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import AuthContext from "../../context/AuthContext";
import { useClient, useUpdateClient, useDeleteClient } from "../../queries/clients";
import { useProjects, useCreateProject, useUpdateProject, useDeleteProject } from "../../queries/projects";
import { ProjectProps, ProjectStatus } from "../../types/interfaces";
import { ApiError } from "../../hooks/apiRequest";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { PageShell } from "../../components/ui/PageShell";
import { StatusTag, ProjectStatusTag } from "../../components/ui/StatusTag";
import { EmptyState } from "../../components/ui/States";
import styles from "./detail.module.css";

const PROJECT_STATUSES: ProjectStatus[] = ["active", "on_hold", "completed", "archived"];

const Fact = ({ label, value }: { label: string; value?: string | null }) => (
  <div className={styles.fact}>
    <span className="q-kicker">{label}</span>
    <span className={`${styles.factValue} ${value ? "" : styles.factEmpty}`}>{value || "Not recorded"}</span>
  </div>
);

export const ClientDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userData } = useContext(AuthContext);
  const isAdmin = userData?.role === "admin";
  const canEdit = isAdmin || userData?.role === "user";

  const { data: client, isLoading, isError, error } = useClient(id!);
  const { data: projects } = useProjects({ clientId: id });
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const deleteProject = useDeleteProject();
  const updateClient = useUpdateClient();
  const deleteClient = useDeleteClient();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<ProjectProps | null>(null);
  const [form] = Form.useForm();
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [clientForm] = Form.useForm();

  const openCreateModal = () => { setEditingProject(null); form.resetFields(); setIsModalOpen(true); };
  const openEditModal = (project: ProjectProps) => {
    form.resetFields(); setEditingProject(project); form.setFieldsValue(project); setIsModalOpen(true);
  };
  const openClientEditModal = () => {
    clientForm.resetFields(); clientForm.setFieldsValue(client); setIsClientModalOpen(true);
  };

  const handleClientSubmit = async () => {
    const values = await clientForm.validateFields();
    try {
      await updateClient.mutateAsync({ id: id!, data: values });
      setIsClientModalOpen(false);
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not save the client");
    }
  };

  const handleClientDelete = () => {
    Modal.confirm({
      title: "Delete this client?",
      content: "Its projects, tasks and time entries stay in reports but the client disappears from every list.",
      okText: "Delete client",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteClient.mutateAsync(id!);
          navigate("/clients");
        } catch (err) {
          message.error(err instanceof ApiError ? err.message : "Could not delete the client");
        }
      },
    });
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    try {
      if (editingProject) await updateProject.mutateAsync({ id: editingProject._id, data: values });
      else await createProject.mutateAsync({ ...values, clientId: id! });
      setIsModalOpen(false);
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not save the project");
    }
  };

  const handleDelete = (projectId: string) => {
    Modal.confirm({
      title: "Delete this project?",
      okText: "Delete project",
      okButtonProps: { danger: true },
      onOk: async () => {
        try { await deleteProject.mutateAsync(projectId); }
        catch (err) { message.error(err instanceof ApiError ? err.message : "Could not delete the project"); }
      },
    });
  };

  if (userData?.role === "final_user") {
    return (
      <PageShell kind="detail" title="Not available">
        <EmptyState
          tone="error"
          title="You don't have access to this page"
          description="Client records are internal. If you need something from this client's account, raise a ticket and we'll pick it up."
          actions={<Button type="primary" onClick={() => navigate("/support")}>Go to support</Button>}
        />
      </PageShell>
    );
  }

  const activeCount = (projects || []).filter((p: ProjectProps) => p.status === "active").length;

  return (
    <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
      <PageShell
        kind="detail"
        crumbs={[{ label: "Clients", to: "/clients" }, { label: client?.name || "Client" }]}
        title={client?.name || ""}
        meta={[
          { label: "Status", value: <StatusTag label={client?.active ? "Active" : "Inactive"} tone={client?.active ? "ok" : "neutral"} dot /> },
          { label: "Projects", value: <span className="q-num">{(projects || []).length}</span> },
          { label: "Active work", value: <span className="q-num">{activeCount}</span> },
        ]}
        actions={
          <Space>
            {canEdit && <Button onClick={openClientEditModal}>Edit client</Button>}
            {isAdmin && <Button danger onClick={handleClientDelete}>Delete</Button>}
          </Space>
        }
      >
        {client && (
          <>
            <div className={styles.split}>
              <div className={styles.main}>
                <div className="q-kicker" style={{ marginBottom: 8 }}>Account</div>
                <p className={styles.lede}>
                  {client.billingAddress
                    ? `Billed to ${client.billingAddress}.`
                    : "No billing address on file yet — add one before the next invoice run."}
                </p>
              </div>
              <aside className={styles.rail}>
                <div className={styles.facts}>
                  <Fact label="Email" value={client.email} />
                  <Fact label="Phone" value={client.phone} />
                  <Fact label="Billing address" value={client.billingAddress} />
                </div>
              </aside>
            </div>

            <section>
              <div className={styles.sectionHead}>
                <span className={styles.sectionTitle}>Projects</span>
                <span className={styles.sectionCount}>{(projects || []).length} total</span>
                {isAdmin && (
                  <div className={styles.sectionActions}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>New project</Button>
                  </div>
                )}
              </div>

              <Table<ProjectProps>
                rowKey="_id"
                size="small"
                dataSource={projects || []}
                pagination={false}
                locale={{
                  emptyText: (
                    <EmptyState
                      title="No projects for this client"
                      description="Create the first project to open a board, a backlog and a place to log time against."
                      actions={isAdmin && <Button type="primary" onClick={openCreateModal}>New project</Button>}
                    />
                  ),
                }}
                columns={[
                  {
                    title: "Name",
                    dataIndex: "name",
                    render: (name: string, record) => (
                      <Button
                        type="link"
                        onClick={() => navigate(`/projects/${record._id}/board`)}
                        style={{ padding: 0, height: "auto", fontWeight: 500 }}
                      >
                        {name}
                      </Button>
                    ),
                  },
                  {
                    title: "Status",
                    dataIndex: "status",
                    width: 140,
                    render: (status: ProjectStatus) => <ProjectStatusTag status={status} />,
                  },
                  {
                    title: "",
                    width: 140,
                    align: "right",
                    render: (_, record) => (
                      <div className={styles.rowActions}>
                        {canEdit && <Button size="small" onClick={() => openEditModal(record)}>Edit</Button>}
                        {isAdmin && <Button size="small" danger onClick={() => handleDelete(record._id)}>Delete</Button>}
                      </div>
                    ),
                  },
                ]}
              />
            </section>
          </>
        )}

        <Modal
          title={editingProject ? "Edit project" : "New project"}
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          onOk={handleSubmit}
          okText={editingProject ? "Save" : "Create project"}
          confirmLoading={createProject.isPending || updateProject.isPending}
        >
          <Form form={form} layout="vertical">
            <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="description" label="Description"><Input.TextArea rows={3} /></Form.Item>
            {editingProject && (
              <Form.Item name="status" label="Status">
                <Select options={PROJECT_STATUSES.map((s) => ({ label: s.replace("_", " "), value: s }))} />
              </Form.Item>
            )}
            <Form.Item name="guideUrl" label="Guide URL"><Input placeholder="https://" /></Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Edit client"
          open={isClientModalOpen}
          onCancel={() => setIsClientModalOpen(false)}
          onOk={handleClientSubmit}
          okText="Save"
          confirmLoading={updateClient.isPending}
        >
          <Form form={clientForm} layout="vertical">
            <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="email" label="Email"><Input /></Form.Item>
            <Form.Item name="phone" label="Phone"><Input /></Form.Item>
            <Form.Item name="billingAddress" label="Billing address"><Input /></Form.Item>
            <Form.Item name="active" label="Active" valuePropName="checked"><Switch /></Form.Item>
          </Form>
        </Modal>
      </PageShell>
    </DisplayComponent>
  );
};
